/**
 * Basic BCP form data storage utility.
 * Manages form data in localStorage for all Basic BCP sections.
 */

import { INITIAL_RESPONSE_SECTIONS } from '../config/initialResponseSections';

export type BasicBcpSectionKey = 'restore_policy' | 'regulation' | 'initial_response' | 'procedure_manual';

interface BasicBcpStorageData {
  bcpId: string;
  tenantId: string | null;
  sections: {
    restore_policy?: Record<string, string>;
    regulation?: Record<string, string>;
    initial_response?: Record<string, string>;
    procedure_manual?: Record<string, string>;
  };
  lastUpdated: string;
}

const STORAGE_KEY_PREFIX = 'basic_bcp_draft_';

/**
 * Get storage key for a specific BCP/tenant combination.
 */
function getStorageKey(bcpId: string, tenantId: string | null): string {
  return `${STORAGE_KEY_PREFIX}${bcpId}_${tenantId || 'default'}`;
}

/**
 * Load all section data for a BCP.
 */
export function loadBasicBcpDraft(
  bcpId: string,
  tenantId: string | null
): BasicBcpStorageData | null {
  try {
    const key = getStorageKey(bcpId, tenantId);
    const stored = localStorage.getItem(key);
    if (!stored) {
      return null;
    }
    return JSON.parse(stored) as BasicBcpStorageData;
  } catch (error) {
    console.error('[BasicBcpStorage] Failed to load draft:', error);
    return null;
  }
}

/**
 * Save section data to localStorage.
 */
export function saveBasicBcpSection(
  bcpId: string,
  tenantId: string | null,
  sectionKey: BasicBcpSectionKey,
  sectionData: Record<string, string>
): void {
  try {
    const key = getStorageKey(bcpId, tenantId);

    // Load existing data
    const existing = loadBasicBcpDraft(bcpId, tenantId) || {
      bcpId,
      tenantId,
      sections: {},
      lastUpdated: new Date().toISOString(),
    };

    // Update section
    existing.sections[sectionKey] = sectionData;
    existing.lastUpdated = new Date().toISOString();

    // Save to localStorage
    localStorage.setItem(key, JSON.stringify(existing));

    console.log(`[BasicBcpStorage] Saved section '${sectionKey}' for BCP ${bcpId}`);
  } catch (error) {
    console.error('[BasicBcpStorage] Failed to save section:', error);
  }
}

/**
 * Load specific section data.
 */
export function loadBasicBcpSection(
  bcpId: string,
  tenantId: string | null,
  sectionKey: BasicBcpSectionKey
): Record<string, string> | null {
  const draft = loadBasicBcpDraft(bcpId, tenantId);
  return draft?.sections[sectionKey] || null;
}

/**
 * Clear all draft data for a BCP.
 */
export function clearBasicBcpDraft(bcpId: string, tenantId: string | null): void {
  try {
    const key = getStorageKey(bcpId, tenantId);
    localStorage.removeItem(key);
    console.log(`[BasicBcpStorage] Cleared draft for BCP ${bcpId}`);
  } catch (error) {
    console.error('[BasicBcpStorage] Failed to clear draft:', error);
  }
}

/**
 * Check if any section has unsaved changes.
 */
export function hasUnsavedChanges(bcpId: string, tenantId: string | null): boolean {
  const draft = loadBasicBcpDraft(bcpId, tenantId);
  if (!draft) {
    return false;
  }

  return Object.keys(draft.sections).length > 0;
}

/**
 * Get list of sections with data.
 */
export function getSectionsWithData(
  bcpId: string,
  tenantId: string | null
): BasicBcpSectionKey[] {
  const draft = loadBasicBcpDraft(bcpId, tenantId);
  if (!draft) {
    return [];
  }

  return Object.keys(draft.sections).filter(key => {
    const section = draft.sections[key as BasicBcpSectionKey];
    return section && Object.keys(section).length > 0;
  }) as BasicBcpSectionKey[];
}

/**
 * Save all sections with localStorage data to the backend.
 * Returns the BCP ID after saving.
 */
export async function saveAllDraftSections(
  bcpId: string,
  tenantId: string | null,
  bcpService: any,
  currentSectionKey?: BasicBcpSectionKey,
  currentSectionData?: Record<string, string>
): Promise<string> {
  const draft = loadBasicBcpDraft(bcpId, tenantId);
  console.log(`[BasicBcpStorage] Loaded draft for BCP ${bcpId}:`, draft ? Object.keys(draft.sections) : 'null');

  let finalBcpId = bcpId;

  // If current section data is provided, save it first
  if (currentSectionKey && currentSectionData) {
    console.log(`[BasicBcpStorage] Saving current section '${currentSectionKey}' with ${Object.keys(currentSectionData).length} fields...`);
    const result = await bcpService.saveBasicSection(
      bcpId === 'new' ? null : bcpId,
      currentSectionKey,
      currentSectionData,
      { current_step: currentSectionKey },
      tenantId || undefined,
      'published' // Save as published so it appears in BCP list
    );
    finalBcpId = result.id;
    console.log(`[BasicBcpStorage] Current section '${currentSectionKey}' saved, BCP ID: ${finalBcpId}`);
  }

  // If no draft data, return early
  if (!draft || Object.keys(draft.sections).length === 0) {
    console.log('[BasicBcpStorage] No draft sections to save');
    return finalBcpId;
  }

  // Save all draft sections sequentially
  const sections = Object.entries(draft.sections);
  console.log(`[BasicBcpStorage] Processing ${sections.length} draft sections...`);

  for (const [sectionKey, sectionData] of sections) {
    console.log(`[BasicBcpStorage] Checking section '${sectionKey}': ${Object.keys(sectionData || {}).length} fields`);

    // Skip current section if already saved
    if (sectionKey === currentSectionKey) {
      console.log(`[BasicBcpStorage] Skipping '${sectionKey}' (already saved as current section)`);
      continue;
    }

    // Skip empty sections
    if (!sectionData || Object.keys(sectionData).length === 0) {
      console.log(`[BasicBcpStorage] Skipping '${sectionKey}' (empty)`);
      continue;
    }

    console.log(`[BasicBcpStorage] Saving draft section '${sectionKey}' with ${Object.keys(sectionData).length} fields...`);
    try {
      const result = await bcpService.saveBasicSection(
        finalBcpId,
        sectionKey,
        sectionData,
        { current_step: sectionKey },
        tenantId || undefined,
        'published' // Save as published so it appears in BCP list
      );
      finalBcpId = result.id;
      console.log(`[BasicBcpStorage] Draft section '${sectionKey}' saved successfully`);
    } catch (error) {
      console.error(`[BasicBcpStorage] Failed to save draft section '${sectionKey}':`, error);
      throw error;
    }
  }

  // Clear localStorage after successful save
  clearBasicBcpDraft(bcpId, tenantId);

  console.log(`[BasicBcpStorage] All draft sections saved successfully, final BCP ID: ${finalBcpId}`);
  return finalBcpId;
}

/**
 * Extract default values from initial response sections config.
 * Returns a dictionary with all default field values.
 */
export function getInitialResponseDefaultValues(): Record<string, string> {
  const defaults: Record<string, string> = {};

  INITIAL_RESPONSE_SECTIONS.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.type !== 'static' && field.defaultValue) {
        defaults[field.id] = field.defaultValue;
      }
    });
  });

  return defaults;
}

/**
 * Generic function to extract default values from any BCP section configuration.
 * Returns a dictionary with all default field values.
 */
export function getDefaultValuesFromSections(
  sections: any[]
): Record<string, string> {
  const defaults: Record<string, string> = {};

  sections.forEach((section) => {
    section.fields?.forEach((field: any) => {
      if (field.type !== 'static' && field.defaultValue) {
        defaults[field.id] = field.defaultValue;
      }
    });
  });

  return defaults;
}
